// =============================================================================
// Admin Analytics — drilldown dialog layout
// =============================================================================
// The shared frame behind both drilldown dialogs (client-side `DrilldownDialog`
// and server-paginated `ServerDrilldownDialog`): a fixed-height body (so it
// never collapses when a filter yields no rows), a pinned filter toolbar,
// sticky column headers, and a paginated table.
//
// Layout: the dialog body is a fixed-height flex column. The header + filter
// toolbar are non-scrolling (always visible); only the table region scrolls, and
// the table's own header row is `sticky top-0` within that region. So both "what
// am I filtering by" and the column labels stay on screen as rows scroll.
//
// Pagination: the Mosaic Table only slices internally when `totalItemCount` is
// omitted. We always pass it and hand the Table exactly the rows to render, so
// the caller owns paging — slicing locally or fetching the page from the server.
// =============================================================================
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";

import { Button, Column, Dialog, Table } from "@fronterahealth/frontera-ui-components";

interface DrilldownLayoutProps<T> {
  title: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Optional content rendered above the filter toolbar (e.g. a legend or summary line). */
  header?: React.ReactNode;
  /** The filter chips. When omitted the toolbar isn't rendered. */
  filterBar?: React.ReactNode;
  /** A short note under the filter chips (e.g. filter options failed to load). */
  filterNotice?: React.ReactNode;
  /** Replaces the table when the rows couldn't load. */
  error?: React.ReactNode;
  /** Left side of the toolbar footer (e.g. "12 of 40 rows"). The footer renders with `onExport`. */
  summary?: React.ReactNode;
  onExport?: () => void;
  exportDisabled?: boolean;
  /** Shown beside the Export button (e.g. export progress, or an export error with a retry). */
  exportStatus?: React.ReactNode;
  rows: T[];
  columns: Column<T>[];
  loading?: boolean;
  totalItemCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  onRowClick?: (row: T) => void;
}

export const DrilldownLayout = <T,>({
  title,
  open,
  setOpen,
  header,
  filterBar,
  filterNotice,
  error,
  summary,
  onExport,
  exportDisabled,
  exportStatus,
  rows,
  columns,
  loading,
  totalItemCount,
  page,
  pageSize,
  onPageChange,
  onRowClick,
}: DrilldownLayoutProps<T>) => (
  <Dialog
    size="5xl"
    title={title}
    open={open}
    setOpen={setOpen}
    primaryButton={undefined}
    secondaryButton={undefined}
    // Fixed height: min == max, so the dialog keeps its size even with 0 rows.
    bodyClassName="h-[80vh]! max-h-[80vh]!"
    overflowBehavior="hidden"
  >
    <div className="flex h-full flex-col gap-4">
      {header && <div className="shrink-0">{header}</div>}

      {filterBar && (
        <div className="flex shrink-0 flex-col gap-3 rounded-lg border border-limestone-100 bg-limestone-50/60 p-3">
          {filterBar}
          {filterNotice}

          {onExport && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-limestone-100 pt-2">
              <span className="text-xs text-text-secondary">{summary}</span>
              <div className="flex items-center gap-2">
                {exportStatus && <span className="text-xs text-text-secondary">{exportStatus}</span>}
                <Button
                  text={
                    <span className="flex items-center gap-1.5">
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      Export CSV
                    </span>
                  }
                  variant="secondary"
                  buttonStyle="border"
                  size="sm"
                  disabled={exportDisabled}
                  onClick={onExport}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Only this region scrolls; the table's header row is sticky within it.
          The Mosaic Table wraps itself in an `overflow-hidden` box, which would
          otherwise become the sticky containing block and pin the header to the
          (scrolling) table instead of this region — so neutralize just that
          wrapper's overflow here, scoped to this dialog. */}
      <div className="aa-drilldown min-h-0 flex-1 overflow-y-auto bg-white">
        <style>{`.aa-drilldown .inline-block.overflow-hidden { overflow: clip !important; }`}</style>
        {error ?? (
          <Table
            data={rows}
            columns={columns}
            loading={loading}
            showPagination
            totalItemCount={totalItemCount}
            defaultItemsPerPage={pageSize}
            currentPageNo={page}
            onPageChange={onPageChange}
            onRowClick={onRowClick}
            cellClassName="py-1.5 px-2 max-w-0 overflow-hidden"
            headingClassName="pl-2! pr-2 sticky top-0 z-10 bg-bg-default"
          />
        )}
      </div>
    </div>
  </Dialog>
);
